import { FileWithPath } from 'react-dropzone';

import {
  DICOM_FILE_EXTENSIONS,
  DROPZONE_ARCHIVE_FORMATS,
} from '@/shared/config';
import { isDicomFile } from '@/shared/lib';

// NOTE: DICOM detection here must stay in sync with the upstream
// `checkFormatValidation` accept list. Both sides read DICOM_FILE_EXTENSIONS
// from `@/shared/config`, so adding a new DICOM extension there propagates to
// both upload validation and this archive filter.
const ARCHIVE_EXTENSIONS = Object.values(DROPZONE_ARCHIVE_FORMATS).flat();

const hasDicomSuffix = (file: FileWithPath) =>
  DICOM_FILE_EXTENSIONS.some((extension) =>
    file.name.toLowerCase().endsWith(extension),
  );

const isExtensionlessCandidate = (file: FileWithPath) =>
  !file.path?.includes('.');

const isArchiveFile = (file: FileWithPath) =>
  ARCHIVE_EXTENSIONS.some((extension) =>
    file.name.toLowerCase().endsWith(extension),
  );

const dropArchives = (files: FileWithPath[]) =>
  files.filter((file) => !isArchiveFile(file));

// NOTE: Sequential reduce with short-circuit so we stop at the first DICOM
// match instead of running 3000 parallel FileReaders.
const someIsDicomByMagicBytes = (files: FileWithPath[]) =>
  files.reduce<Promise<boolean>>(
    async (accPromise, file) => (await accPromise) || isDicomFile(file),
    Promise.resolve(false),
  );

export const filterArchivesIfDicomPresent = async (
  files: FileWithPath[],
): Promise<FileWithPath[]> => {
  if (!files.some(isArchiveFile)) {
    return files;
  }

  if (files.some(hasDicomSuffix)) {
    return dropArchives(files);
  }

  const candidates = files.filter(isExtensionlessCandidate);

  if (candidates.length === 0) {
    return files;
  }

  const hasDicomMagic = await someIsDicomByMagicBytes(candidates);

  return hasDicomMagic ? dropArchives(files) : files;
};
